import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useSessionStore } from "@/stores/sessionStore";
import { useTweaksStore } from "@/stores/tweaksStore";
import { useToggleStar } from "@/hooks/useSidebarFlavors";
import { useFormulationVariants } from "@/hooks/useFormulationVariants";
import { useFormulationStats } from "@/hooks/useFormulationStats";
import { useFlavorSession } from "@/hooks/useFlavorSession";
// DEMO (impl-demo-mock) — remove with services/demo.
import { demoConcDefault, demoForceAddableSet, demoHiddenIngredients } from "@/services/demo/hint";
import { useCompareSelection } from "@/hooks/useCompareSelection";
import {
  useFlavorComposition,
  flavorCompositionKeys,
} from "@/hooks/useFlavorComposition";
import { compareSuggestionKeys } from "@/hooks/useCompareSuggestions";
import { useFlavorFormulas, flavorFormulaKeys } from "@/hooks/useFlavorFormulas";
import { useAutosaveFormula } from "@/hooks/useAutosaveFormula";
import {
  useIngredientSearch,
  useIngredientFamilies,
  useIngredientRoleCounts,
} from "@/hooks/useIngredientSearch";
import { EMPTY_INGREDIENT_FILTERS } from "@/utils/types/ingredient";
import { normalizeAmountUnit } from "@/utils/formulation/normalizeAmountUnit";
import { mergeFormulationAdditions } from "@/utils/formulation/mergeFormulationAdditions";
import { useQueryClient } from "@tanstack/react-query";
import {
  archiveFlavorFormula,
  createFlavorFormula,
  renameFlavorFormula,
  type AmountUnit,
} from "@/services/formula_api";
import type { PerItemReplacements, VizPayload } from "@/services/workbench_api";
import { fetchPreviewViz } from "@/services/preview_api";
import { fetchRecompute } from "@/services/recompute_api";
// DEMO (impl-demo-mock) — remove with services/demo.
import type { VertexRecommendationRow } from "@/services/vertexRecommendations_api";
import { parseSubstanceKey } from "@/utils/types/substance";
import {
  IngredientSearchSkeleton,
  FlaskPanelSkeleton,
  SensoryRailSkeleton,
} from "@/components/Workbench/Skeletons";
import { WorkbenchTopBar } from "@/components/Workbench/TopBar/WorkbenchTopBar";
import {
  FormulationTabsBar,
  FormulationPromptPopover,
} from "@/components/FormulationTabs";
import { FlaskPanel } from "@/components/Workbench/Flask/FlaskPanel";
import { ComparePanel } from "@/components/Workbench/Compare";
import { IngredientSearch } from "@/components/Workbench/IngredientSearch/IngredientSearch";
import {
  SensoryAnalysisRail,
  type ActiveRadarSelection,
} from "@/components/Workbench/RightRail/SensoryAnalysisRail";
import { MakePhase } from "./MakePhase";
import { AssessPhase } from "./AssessPhase";
import { PilotBarrier } from "@/components/PilotLock";
import { useBatchStore } from "@/stores/batchStore";
import type { ReplacementPreview } from "@/utils/formulation/computeCurrent";
import { useAutoFormulate, type AutoFormulateState } from "@/hooks/useAutoFormulate";
import { useWorkbenchReplacements } from "@/hooks/useWorkbenchReplacements";
import { useWorkbenchViz } from "@/hooks/useWorkbenchViz";
import { useCostGlobals } from "@/hooks/useCostGlobals";
import { useSubstanceSupplyFlags } from "@/hooks/useSubstanceSupplyFlags";
import { computeCost, type CostInputs } from "@/utils/formulation/computeCost";
import { logEvent } from "@/lib/eventLogger";
import monitoring from "@/lib/monitoring";
import { lookupServerCompound } from "@/utils/serverCompounds";
import { COMPOUND_LIB, FOUNDATION_COMPOUNDS } from "@/services/mock";
import type { Phase } from "@/utils/types/ui";
import type { FormulationEntry } from "@/utils/types/compound";
import type { VersionDetail } from "@/utils/types/flavorVersion";

const VALID_PHASES: Phase[] = ["design", "make", "assess"];

// Workbench root sizing — keeps the three-column design layout legible at
// narrow viewports. Below this the workbench scrolls horizontally rather
// than letting columns squeeze themselves into illegibility.
//
// The floor is set by the centre column: the FlaskPanel header carries the
// "Current flask" label plus 5 toolbar buttons (Composition · Compare ·
// History · Import · Export). At widths below ~640px the toolbar starts
// overflowing past the panel's right edge, so we floor centre at 640 and
// size the workbench accordingly: 320 (left max) + 640 (centre min) +
// 400 (right fixed = SENSORY_RAIL_WIDTH) + gaps/padding ≈ 1380.
const WORKBENCH_MIN_WIDTH = 1380;
const FLASK_COL_MIN = 640;
const SENSORY_RAIL_WIDTH = 400;

// Outer wrapper exists only to give the inner component a key tied to the
// URL flavor id. Without the key, navigating from /flavors/A to /flavors/B
// would keep the same WorkbenchPageInner instance — and `useFormulationVariants`'
// `seededFromServerRef` would stay `true`, so flavor B keeps showing flavor A's
// variants (formulaId, items, etc.) until a hard reload. The autosave hook
// then fires `POST /me/flavor/B/formulas/<A's formulaId>/versions`, which
// 404s. Remounting on `id` change resets all that local state cleanly.
export function WorkbenchPage() {
  const { id } = useParams<{ id: string }>();
  return <WorkbenchPageInner key={id ?? "no-id"} />;
}

function WorkbenchPageInner() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const session = useSessionStore((s) => s.session);
  const starred = useSessionStore((s) => s.starred);
  const toggleStarred = useSessionStore((s) => s.toggleStarred);
  const toggleStar = useToggleStar();

  const { id: rawIdParam } = useParams<{ id: string }>();
  const isTempId = rawIdParam?.startsWith("new-") ?? false;
  // flavor.id is now a uuid; URLs hand it through as-is. Empty string isn't
  // a valid uuid but covers the no-id case.
  const validId = !isTempId && rawIdParam ? rawIdParam : null;

  // Fire once per workbench mount per non-temp flavor id. The outer
  // WorkbenchPage `key={id}` remounts this component on navigation, so this
  // effect re-runs (with the new id) on flavor switches.
  useEffect(() => {
    if (!validId) return;
    logEvent("flavor_workbench_opened", { flavor_id: validId });
  }, [validId]);

  // Skip server fetch when:
  //   1. URL is a temp id (new-*) — created flow puts the session in localStorage
  //   2. session.id already matches the url id — fresh in-memory copy
  const shouldFetch =
    validId !== null && (!session || session.id !== validId);

  const hydrate = useFlavorSession(shouldFetch ? validId : null);
  // True once the parent flavor lookup has been confirmed scoped-to-this-user
  // — either via a successful /me/flavor/:id hydrate, or by sessionStore
  // already holding the flavor (the `!shouldFetch` branch — no fetch needed).
  // Every dependent React Query call below gates on this so a 400/404/403 on
  // the parent doesn't fan out into a shotgun of doomed sibling requests.
  const flavorReady = validId !== null && (!shouldFetch || hydrate.isSuccess);
  // Gated dependent. Same /formulas endpoint as before; only fires once the
  // parent flavor is confirmed loadable. React Query's `staleTime: 30_000`
  // (in useFlavorFormulas) suppresses redundant fetches for repeated visits.
  const formulasQ = useFlavorFormulas(flavorReady ? validId : null);
  // DEMO (impl-demo-mock) — scenario hint (CONC defaults + force-addable),
  // null for non-demo flavors. Rides on the always-fetched formulas response.
  const demoHint = formulasQ.data?.demo ?? null;

  // Bounce conditions:
  //   - URL is malformed (not new-* and not a positive int)
  //   - Server returned 404 / network error during hydration
  //   - Temp id with no in-memory session (user landed here directly)
  const shouldBounce =
    (!isTempId && validId === null) ||
    (isTempId && !session) ||
    hydrate.isError;

  const tabStyle = useTweaksStore((s) => s.tabStyle);
  const tabShowKpi = useTweaksStore((s) => s.tabShowKpi);

  const phaseParam = searchParams.get("phase") as Phase | null;
  const phase: Phase =
    phaseParam && VALID_PHASES.includes(phaseParam) ? phaseParam : "design";

  const queryClient = useQueryClient();

  const {
    variants,
    activeVariantId,
    activeVariant,
    isSeeded: variantsSeeded,
    setActiveVariantId,
    setFormulation,
    patchVariant,
    forkVariant,
    renameVariant,
    removeVariant,
  } = useFormulationVariants({
    initialFormulation: session?.formulation || [],
    initialVariants: formulasQ.variants,
    callbacks: {
      onFork: (newVar, source) => {
        if (validId === null) return;
        void createFlavorFormula(validId, {
          prompt: newVar.prompt,
          parentFormulaId: source.formulaId ?? null,
        })
          .then((created) => {
            logEvent("flavor_formula_created", {
              flavor_id: validId,
              formula_id: created.formulaId,
              formula_number: created.formulaNumber,
              parent_formula_id: source.formulaId ?? null,
            });
            // Patch the local variant with the server-assigned ids so
            // autosave / rename / remove guards stop no-op'ing on this tab.
            patchVariant(newVar.id, {
              formulaId: created.formulaId,
              formulaNumber: created.formulaNumber,
              currentVersionId: created.versionId,
            });
            return queryClient.invalidateQueries({
              queryKey: flavorFormulaKeys.byFlavor(validId),
            });
          })
          .catch((err) => monitoring.warn("flavor_fork_failed", err instanceof Error ? err : new Error(String(err))));
      },
      onRename: (variant, name) => {
        if (validId === null || !variant.formulaId) return;
        void renameFlavorFormula(validId, variant.formulaId, name)
          .then(() =>
            queryClient.invalidateQueries({
              queryKey: flavorFormulaKeys.byFlavor(validId),
            }),
          )
          .catch((err) => monitoring.warn("flavor_rename_failed", err instanceof Error ? err : new Error(String(err))));
      },
      onRemove: (variant) => {
        if (validId === null || !variant.formulaId) return;
        logEvent("flavor_formula_deleted", {
          flavor_id: validId,
          formula_id: variant.formulaId,
        });
        void archiveFlavorFormula(validId, variant.formulaId)
          .then(() =>
            queryClient.invalidateQueries({
              queryKey: flavorFormulaKeys.byFlavor(validId),
            }),
          )
          .catch((err) => monitoring.warn("flavor_archive_failed", err instanceof Error ? err : new Error(String(err))));
      },
    },
  });

  // Pre-fetch the ingredient queries IngredientSearch will need on first mount.
  // IngredientSearch's `useIngredientSearch(...)` includes `versionId` in its
  // query key (it drives the ingredient_sort → delta_loss_sq JOIN on the server),
  // so the prefetch MUST pass the same versionId to share the cache slot.
  //
  // We call the prefetch hooks AFTER `useFormulationVariants` so
  // `activeVariant?.currentVersionId` is in scope. The hooks themselves still
  // run unconditionally — they just receive a null versionId on the first
  // render (before formulas land) and the real id once variants are seeded.
  // Without this versionId pass-through, the prefetch's cache key was
  // `[..., null]` while the panel's was `[..., 123]`, so the prefetch did
  // nothing useful AND the freshly-created flavor's Add Ingredients panel
  // briefly rendered the wrong (no-delta_loss_sq) data via `keepPreviousData`.
  const prefetchVersionId = activeVariant?.currentVersionId ?? null;
  const gatedFlavorId = flavorReady ? validId : null;
  // Defer ingredient-search + role-counts until variants are seeded, so the
  // first request always carries the real versionId. Without this, the hook
  // fires as soon as flavor hydration finishes (well before useFormulationVariants
  // resolves), versionId is null, the server's ingredient_sort_for_version CTE
  // is empty, every compound's delta_loss_sq is NULL, and the rows sort
  // alphabetically. `keepPreviousData` then keeps the alphabetical result on
  // screen across the queryKey flip from null → real versionId, so the user
  // sees a 1-2s flash of unranked results.
  const searchReadyFlavorId =
    flavorReady && variantsSeeded ? validId : null;
  const ingredientSearch = useIngredientSearch(
    searchReadyFlavorId,
    EMPTY_INGREDIENT_FILTERS,
    prefetchVersionId,
  );
  const ingredientFamilies = useIngredientFamilies(gatedFlavorId);
  const ingredientRoleCounts = useIngredientRoleCounts(
    searchReadyFlavorId,
    EMPTY_INGREDIENT_FILTERS,
    prefetchVersionId,
  );

  // Keep the skeleton up until BOTH the Current Flask (variants seeded from
  // the server) AND the Add Ingredients panel (search, families, role-counts
  // first page loaded) are ready. Load-then-show — never show a half-loaded
  // workbench.
  //
  // `isLoading` (not `isPending`) — for disabled queries (e.g. validId is
  // null in temp-id mode) `isLoading` stays false, so we don't lock the
  // skeleton on the new-flavor draft path. shouldFetch already guards the
  // whole expression for that case too.
  //
  // Batch-supply-flags / lens enrichment can still lazily arrive AFTER the
  // skeleton drops — they only colorize stripes and add supply icons; the
  // row scaffold renders fine without them.
  //
  // NOTE: must be evaluated AFTER useFormulationVariants destructures
  // `variantsSeeded`. Reading it before the destructure was a TDZ
  // ReferenceError that only triggered when `shouldFetch` was truthy
  // (i.e. when the user switched between flavors and the session-store
  // hadn't rehydrated yet).
  const isHydrating =
    shouldFetch &&
    ((hydrate.isLoading && !session) ||
      (formulasQ.isLoading && !formulasQ.variants) ||
      !variantsSeeded ||
      ingredientSearch.isLoading ||
      ingredientFamilies.isLoading ||
      ingredientRoleCounts.isLoading);

  // Wire autosave to the active variant. Saves are debounced inside the hook.
  // markSynced is invoked after server-confirmed updates (import-commit,
  // restore) so the patch they perform doesn't trigger a redundant autosave.
  // onVersionSaved threads the new versionId into the active variant so the
  // Add Ingredients search + viz hook re-query against the latest version.
  //
  // The save response carries the inline-computed viz payload — pre-populate
  // the React Query cache for ["viz", versionId] so the SensoryAnalysisRail
  // (and anything else reading useWorkbenchViz) renders the new version's
  // viz immediately, without a follow-up /viz fetch. Same idea protects
  // /ingredient-search?versionId=<new>: by the time currentVersionId
  // propagates and the search refetches, the server-side ingredient_sort
  // is already populated (computeViz ran INLINE in the POST), so the
  // delta-loss values land on the first response — no flicker.
  // Ref to aiState — autosave's onVersionSaved is declared above aiState
  // below (autosave's `pause`/`resume`/`flush` are inputs to aiState), so
  // we can't reference aiState in onVersionSaved's closure directly. The
  // ref is populated immediately after aiState is created and read at
  // save-completion time to drop the AF proposal overlay in lockstep
  // with currentVersionId being patched.
  const aiStateRef = useRef<AutoFormulateState | null>(null);

  const autosave = useAutosaveFormula({
    flavorId: validId,
    activeVariant: activeVariant ?? null,
    // aiStateRef is populated right after aiState is created (below). Reading
    // `.phase` here reflects the prior render's commit; on the render where AF
    // flips to 'review' the ref already holds it, so reviewMode becomes true
    // within one render — fine, the entry-recompute effect keys on the change.
    reviewMode: aiStateRef.current?.phase === "review",
    onReviewRecompute: (items, signal) => {
      const fid = validId;
      const formulaId = activeVariant?.formulaId ?? null;
      const versionId = activeVariant?.currentVersionId ?? null;
      if (fid === null || formulaId === null || versionId === null) return;
      const reqItems = items.map((it) => ({
        substanceKind: it.substanceKind,
        substanceId: it.substanceId,
      }));
      void fetchRecompute(
        { flavorId: fid, formulaId, versionId, items: reqItems },
        signal,
      )
        .then(({ previewId, viz }) => {
          aiStateRef.current?.setReviewResult(previewId, viz);
        })
        .catch((err: unknown) => {
          if ((err as { name?: string } | null)?.name === "AbortError") return;
          monitoring.warn(
            "workbench_review_recompute_failed",
            err instanceof Error ? err : new Error(String(err)),
          );
        });
    },
    onVersionSaved: (variantId, response) => {
      patchVariant(variantId, { currentVersionId: response.versionId });
      if (response.viz) {
        queryClient.setQueryData(["viz", response.versionId], response.viz);
      }
      // If this save was triggered by AF Accept (aiState.accept() called
      // flush()), drop the AF state now so the proposed-viz override gives
      // way to the freshly-saved viz in the same render commit that
      // currentVersionId gets patched. Without this, the override would
      // linger until the 30s safety-net timeout — the orange Point/Radar
      // wouldn't transition to green.
      aiStateRef.current?.finalize();
    },
  });

  const target = session?.target ?? null;
  const formulation = activeVariant?.formulation || [];

  const flavorKey = session?.id || session?.label || "default";
  const lockedVariantIds = useBatchStore((s) => s.lockedFor(flavorKey));
  const activeLocked = lockedVariantIds.includes(activeVariantId);

  // Replacement preview state — populated by the inline ReplacementSubRows
  // tray (rendered inside FlaskPanel) when the user picks a candidate.
  const [preview, setPreview] = useState<ReplacementPreview | null>(null);
  const [previewViz, setPreviewViz] = useState<VizPayload | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  // DEMO (impl-demo-mock) — viz/loss override shown after the user applies a
  // vertex recommendation (Stage #10), mocked from the original pre-swap
  // ingredient. Persists across the rec's own autosave; cleared on vertex
  // change / remove / AF / preview. Remove with src/demo.
  const [demoRecViz, setDemoRecViz] = useState<VizPayload | null>(null);
  const demoRecAbortRef = useRef<AbortController | null>(null);

  // Composition aside toggle. Lifted here so it survives panel remounts and
  // can be addressed from the workbench level when needed.
  const [showComposition, setShowComposition] = useState(false);

  // Active vertex selection from the Sensory Radar. Lifted here because two
  // consumers — IngredientSearch's pinned "Recommended for {vertex}" panel
  // and (eventually) anything else that wants to react to the user's
  // hotspot focus — both need the same source of truth, and the radars
  // themselves live in a different subtree from the search panel.
  const [radarSelection, setRadarSelection] =
    useState<ActiveRadarSelection | null>(null);
  // Stable identity: SensoryAnalysisRail keys a selection-cleanup effect on
  // this prop and calls it from the effect's teardown. An inline arrow would
  // get a fresh identity every render, re-running that teardown and instantly
  // clearing the selection (the "Recommended" panel flashes open then closes).
  // DEMO (impl-demo-mock) — also drops the applied-rec viz mock on vertex
  // change / rail unmount.
  const handleActiveSelectionChange = useCallback(
    (sel: ActiveRadarSelection | null) => {
      setDemoRecViz(null);
      setRadarSelection(sel);
    },
    [],
  );

  // Auto-Formulate orchestrator — drives the run/review state machine.
  // The hook needs a bulk setter so it can apply the planner's actions to
  // the active variant; we adapt the variant-aware setFormulation into a
  // pure `(next) => void` for the hook.
  const replaceFormulation = useCallback(
    (next: FormulationEntry[]) => setFormulation(() => next),
    [setFormulation],
  );
  const resolveCompoundName = useCallback((id: string) => {
    const found =
      [...COMPOUND_LIB, ...FOUNDATION_COMPOUNDS].find((c) => c.id === id) ??
      lookupServerCompound(id);
    return found?.name ?? id;
  }, []);
  // Live workbench viz (live formulation's loss² + target + current profile
  // vectors). The AIBanner reads loss + targetProfile + currentProfile from
  // this; SensoryAnalysisRail also reads it via its own useWorkbenchViz
  // call. Duplicated query call is harmless — staleTime is Infinity, so the
  // second consumer just reads from cache.
  const workbenchVizQ = useWorkbenchViz({
    flavorId: gatedFlavorId,
    formulaId: activeVariant?.formulaId ?? null,
    versionId: activeVariant?.currentVersionId ?? null,
  });
  const liveViz = workbenchVizQ.data;

  // Hoist the AF-relevant subset of liveViz so useAutoFormulate can snapshot
  // it at run() time (used as the start point for the apply-phase
  // interpolation toward proposedViz).
  const liveVizForAf = useMemo(
    () =>
      liveViz
        ? {
            profile: liveViz.profile,
            drCoord: liveViz.drCoord,
            loss: liveViz.loss,
            tasteRadar: liveViz.tasteRadar,
            smellRadar: liveViz.smellRadar,
          }
        : null,
    [liveViz],
  );

  const aiState = useAutoFormulate({
    flavorId: validId,
    formulaId: activeVariant?.formulaId ?? null,
    versionId: activeVariant?.currentVersionId ?? null,
    formulation: activeVariant?.formulation ?? [],
    setFormulation: replaceFormulation,
    pauseAutosave: autosave.pause,
    resumeAutosave: autosave.resume,
    flushAutosave: autosave.flush,
    resolveName: resolveCompoundName,
    liveViz: liveVizForAf,
  });
  // Bridge to autosave.onVersionSaved (declared above aiState). See ref
  // comment near `aiStateRef` initialization.
  aiStateRef.current = aiState;
  // Feed previewId to the Add-Ingredients surfaces during Review AND the
  // 'saving' phase that follows Accept. During 'saving' the new version hasn't
  // landed yet (currentVersionId is still the empty base v1), so dropping the
  // previewId here would briefly query the empty base version and flash its
  // generic ingredient list. Holding the preview keeps the Review-stage list
  // visible until currentVersionId is patched to the accepted version (where
  // the same pinned list is served via the saved path).
  const reviewPreviewId =
    aiState.phase === "review" || aiState.phase === "saving"
      ? aiState.reviewPreviewId
      : null;
  const handleAutoFormulate = useCallback(
    (opts: { mode: "loss" | "cost" | "prompt"; prompt?: string }) => {
      setDemoRecViz(null); // DEMO (impl-demo-mock) — drop the applied-rec mock
      logEvent("flavor_workbench_auto_formulate_started", {
        flavor_id: validId,
        formula_id: activeVariant?.formulaId ?? null,
        version_id: activeVariant?.currentVersionId ?? null,
        mode: opts.mode,
        prompt: opts.prompt?.trim() || null,
      });
      return aiState.run(opts);
    },
    [aiState, validId, activeVariant?.formulaId, activeVariant?.currentVersionId],
  );

  // Build the AF viz override — fires during BOTH the staged `applying`
  // phase (interpolated viz from useAutoFormulate.inflightViz) and the
  // post-apply `review` phase (final proposedViz). The override carries
  // proposal-specific values (drCoord, loss, radars, profile) but
  // reuses target / neighbors / axes from the live viz so the
  // food-landscape background stays intact.
  // When AF is idle, a user-initiated preview (previewViz) fires the same
  // orange overlay so the Sensory Radar + Flavor Space show the hypothetical
  // swap before the user commits.
  const aiVizOverride = useMemo<VizPayload | null>(() => {
    if (!liveViz) return null;

    // Review with a live recompute → use the full VizPayload directly (its
    // axes.taste.detail is already computed for the edited proposal). Before
    // the first recompute resolves, reviewViz is null and we fall through to
    // the proposedViz path below (so the proposal shows immediately on entry).
    if (aiState.phase === "review" && aiState.reviewViz) {
      const rv = aiState.reviewViz;
      return {
        ...liveViz,
        status: "done",
        profile: rv.profile,
        drCoord: rv.drCoord,
        loss: rv.loss,
        tasteRadar: rv.tasteRadar,
        smellRadar: rv.smellRadar,
        axes: rv.axes,
      } satisfies VizPayload;
    }

    // AF takes precedence over a user preview — AF state machine drives
    // its own orange overlay, can't fight with previewViz.
    const afSource =
      aiState.phase === "applying"
        ? aiState.inflightViz
        : aiState.phase === "review" || aiState.phase === "saving"
          ? aiState.proposedViz
          : null;
    if (afSource) {
      // Rebuild taste detail.current from the in-flight profile so the
      // drilled "Receptor-Detail" radar moves with each AI step. AF wire
      // shape only carries profile + receptor-level radars; the dim-index
      // mapping lives on the static /viz response. Target stays — it
      // doesn't change during AF.
      const afProfile = afSource.profile;
      const tasteDetail = afProfile
        ? Object.fromEntries(
            Object.entries(liveViz.axes.taste.detail).map(([k, d]) => [
              k,
              {
                ...d,
                current: d.dimIdxs.map((i) =>
                  i < afProfile.length ? afProfile[i]! : 0,
                ),
              },
            ]),
          )
        : liveViz.axes.taste.detail;
      return {
        ...liveViz,
        status: "done",
        profile: afSource.profile,
        drCoord: afSource.drCoord,
        loss: afSource.loss,
        tasteRadar: afSource.tasteRadar,
        smellRadar: afSource.smellRadar,
        axes: {
          ...liveViz.axes,
          taste: { ...liveViz.axes.taste, detail: tasteDetail },
        },
      } satisfies VizPayload;
    }

    // DEMO (impl-demo-mock) — applied-recommendation override (Stage #10),
    // mocked from the original ingredient. Mirrors the previewViz branch below.
    // Remove with src/demo.
    if (demoRecViz) {
      return {
        ...liveViz,
        status: "done",
        profile: demoRecViz.profile,
        drCoord: demoRecViz.drCoord,
        loss: demoRecViz.loss,
        tasteRadar: demoRecViz.tasteRadar,
        smellRadar: demoRecViz.smellRadar,
        axes: demoRecViz.axes,
      } satisfies VizPayload;
    }

    if (previewViz) {
      // Preview already came back as a full VizPayload, so its
      // axes.taste.detail is server-computed for the swap — use it
      // directly. (Spreading ...liveViz first would clobber it with
      // pre-preview detail values.)
      return {
        ...liveViz,
        status: "done",
        profile: previewViz.profile,
        drCoord: previewViz.drCoord,
        loss: previewViz.loss,
        tasteRadar: previewViz.tasteRadar,
        smellRadar: previewViz.smellRadar,
        axes: previewViz.axes,
      } satisfies VizPayload;
    }

    return null;
  }, [aiState.phase, aiState.inflightViz, aiState.proposedViz, aiState.reviewViz, previewViz, demoRecViz, liveViz]);

  const variantStats = useFormulationStats(variants, target);

  const { data: replacements } = useWorkbenchReplacements({
    flavorId: gatedFlavorId,
    formulaId: activeVariant?.formulaId ?? null,
    versionId: activeVariant?.currentVersionId ?? null,
  });

  // Build per-compound map from the replacements payload. perItem is keyed
  // by FFVI id; translate to server compoundId ("compound:<rowid>") using the
  // active formula's items from formulasQ.data.
  const { perItemByCompoundId } = useMemo<{
    perItemByCompoundId: Record<string, PerItemReplacements>;
  }>(() => {
    if (!replacements || replacements.status !== "ready") {
      return { perItemByCompoundId: {} };
    }
    const activeFormulaId = activeVariant?.formulaId;
    if (!activeFormulaId) {
      return { perItemByCompoundId: {} };
    }
    const activeFormulaItems = formulasQ.data?.formulas.find(
      (f) => f.id === activeFormulaId,
    )?.items;
    if (!activeFormulaItems) {
      return { perItemByCompoundId: {} };
    }
    const perItemMap: Record<string, PerItemReplacements> = {};
    for (const [ffviIdStr, perItem] of Object.entries(replacements.perItem)) {
      const ffviId = Number(ffviIdStr);
      const item = activeFormulaItems.find((it) => it.id === ffviId);
      if (!item) continue;
      perItemMap[item.compoundId] = perItem;
    }
    return { perItemByCompoundId: perItemMap };
  }, [replacements, activeVariant?.formulaId, formulasQ.data]);

  // Compare selection — catalog + selected list + sensory overlays. Resets
  // automatically whenever the active variant changes.
  const {
    catalog: compareCatalog,
    selected: compareSelected,
    toggle: onCompareToggle,
    clear: onCompareClear,
    clearSlot: onCompareClearSlot,
    computeStats: computeCompareStats,
    sensory: compareSensory,
    suggestionsLoading: compareSuggestionsLoading,
  } = useCompareSelection({
    flavorId: gatedFlavorId,
    variants,
    activeVariantId,
    target,
  });

  // Server-driven composition state. Drives the Composition button
  // (enabled/disabled + tooltip) and the CompositionAside contents.
  const compositionQ = useFlavorComposition(gatedFlavorId);
  const composition = compositionQ.data;
  const hasComposition =
    composition?.hasTargetFood === true && composition.rows.length > 0;
  const compositionRows = composition?.hasTargetFood ? composition.rows : [];
  const compositionFoodName = composition?.hasTargetFood
    ? composition.food.name
    : null;
  const compositionNfoodVersionId = composition?.hasTargetFood
    ? composition.nfoodVersionId
    : null;

  // Invalidate composition + compare-suggestions if the flavor's TARGET
  // reference changes without the URL flavor id changing. The URL-id
  // remount (key={id} on the outer wrapper) already handles flavor
  // switches; this catches future in-place target reassignments.
  useEffect(() => {
    if (validId === null) return;
    queryClient.invalidateQueries({
      queryKey: flavorCompositionKeys.byFlavor(validId),
    });
    queryClient.invalidateQueries({
      queryKey: compareSuggestionKeys.byFlavor(validId),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validId, session?.target]);

  // Clear preview state whenever the active version changes (e.g. after an
  // autosave that patches currentVersionId). The previewed swap referred to
  // the old version's compound set; keeping it would show stale orange overlays.
  useEffect(() => {
    if (previewViz || preview) {
      previewAbortRef.current?.abort();
      setPreviewViz(null);
      setPreview(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant?.currentVersionId]);

  // displayFormulation (post-preview-replacement view) was only consumed by
  // the now-removed computeCurrent → computeLoss pipeline. The server
  // computes loss off the SAVED version, so the live preview doesn't
  // contribute to the readout anyway — we drop the local recomputation.

  // Loss is server-authored, NOT the synthetic computeLoss(computeCurrent…).
  // Same query the SensoryAnalysisRail reads — React Query dedups by
  // ['viz', versionId] so we share a single network call.
  //
  // Under the 2026-05-26 zero-seed convention, the server returns the
  // baseline loss (Σ W·T²) when the formulation is empty AND a target
  // exists — LossTile renders the number; the user sees their starting
  // distance from target before adding any compound. loss is only
  // null when there's no target.
  const { data: vizData } = useWorkbenchViz({
    flavorId: gatedFlavorId,
    formulaId: activeVariant?.formulaId ?? null,
    versionId: activeVariant?.currentVersionId ?? null,
  });
  const liveLoss = vizData?.loss ?? null;

  const [useMedianFallback, setUseMedianFallback] = useState(false);
  const costGlobalsQ = useCostGlobals(gatedFlavorId, useMedianFallback);
  const medianPricePerGramUsd = costGlobalsQ.data?.globalMedianPricePerGramUsd ?? null;

  const activeSupplyKeys = useMemo(
    () => Array.from(new Set([
      ...formulation.map((f) => f.compoundId),
      ...(activeVariant?.fixedIngredients ?? []).map((f) => f.compoundId),
      // While a replacement preview is open, fetch the previewed candidate(s)
      // too so their price feeds the preview cost (mirrors previewViz → loss).
      ...(preview?.toIds ?? []),
    ])),
    [formulation, activeVariant?.fixedIngredients, preview],
  );
  const activeFlagsQ = useSubstanceSupplyFlags(gatedFlavorId, activeSupplyKeys);
  const activeFlagsBySubstance = activeFlagsQ.data;

  const liveCost = useMemo(() => {
    const m = new Map<string, CostInputs>();
    for (const id of activeSupplyKeys) {
      const f = activeFlagsBySubstance?.[id];
      m.set(id, f?.costInputs ?? {
        pricePerGramUsd: null, mw: null, densityGPerMl: null,
      });
    }
    return computeCost(formulation, m, {
      useMedianFallback,
      medianPricePerGramUsd,
    });
  }, [formulation, activeSupplyKeys, activeFlagsBySubstance, useMedianFallback, medianPricePerGramUsd]);

  // Replacement-preview cost. The cost tile must move on a previewed swap the
  // same way the loss tile does — loss bridges via previewViz from the server;
  // cost is a pure client recompute over the hypothetical formulation. Build it
  // by swapping the replaced row out and the candidate(s) in at the replaced
  // row's amount (split evenly for a multi-swap; loss is concentration-
  // independent server-side, but cost needs a concrete ppm). null = no preview.
  const previewCost = useMemo(() => {
    if (!preview) return null;
    const fromEntry = formulation.find((f) => f.compoundId === preview.fromId);
    const rest = formulation.filter((f) => f.compoundId !== preview.fromId);
    const previewFormulation: FormulationEntry[] =
      preview.isDrop || preview.toIds.length === 0
        ? rest
        : mergeFormulationAdditions(
            rest,
            preview.toIds.map((id) => ({
              compoundId: id,
              ppm: (fromEntry?.ppm ?? 0) / preview.toIds.length,
              amountUnit: fromEntry?.amountUnit,
            })),
          );
    const m = new Map<string, CostInputs>();
    for (const id of activeSupplyKeys) {
      const f = activeFlagsBySubstance?.[id];
      m.set(id, f?.costInputs ?? {
        pricePerGramUsd: null, mw: null, densityGPerMl: null,
      });
    }
    return computeCost(previewFormulation, m, {
      useMedianFallback,
      medianPricePerGramUsd,
    });
  }, [preview, formulation, activeSupplyKeys, activeFlagsBySubstance, useMedianFallback, medianPricePerGramUsd]);

  // DEMO (impl-demo-mock) — pinned rows stay clickable; once a conc-default
  // compound is added at its default (δ-decalactone @50 ppm) it's HIDDEN from
  // the Add-Ingredient list on the next refetch. Logic lives in services/demo.
  // Remove with it.
  const demoForceAddable = useMemo(() => demoForceAddableSet(demoHint), [demoHint]);
  const demoHidden = useMemo(
    () => demoHiddenIngredients(demoHint, formulation),
    [demoHint, formulation],
  );

  const promptBtnRef = useRef<HTMLButtonElement | null>(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [promptAnchorRect, setPromptAnchorRect] = useState<DOMRect | null>(null);
  const openPrompt = () => {
    if (promptBtnRef.current) {
      setPromptAnchorRect(promptBtnRef.current.getBoundingClientRect());
    }
    setPromptOpen(true);
  };

  // Shared helper — maps a VersionDetail back to the variant's in-memory
  // formulation. Used by both restore (History) and commit (Import).
  const applyVersionDetail = useCallback(
    (detail: VersionDetail) => {
      // Both compounds AND foods are valid formulation entries — the active
      // variant may carry either kind. FormulationEntry.compoundId is the
      // namespaced server key ('compound:<rowid>' | 'food:<rowid>'), which
      // downstream consumers (useSubstanceSupplyFlags / batch-supply-flags)
      // pattern-match on /^(compound|food):[0-9]+$/.
      //
      // amountUnit is sourced from the saved row's amount_unit so a non-ppm
      // import (e.g. mg) round-trips correctly through the FlaskPanel.
      // Filter to category=FLAVOR rows only — FIXED_INGREDIENT entries belong
      // to the variant's `fixedIngredients` slot, not the active formulation.
      // Normalize the (value, unit) pair to the best-fit unit in its chain
      // before seating it on the variant — same rule as dtoToVariant on the
      // initial-load path. Without this, a restored "0.5 ppm" row would
      // render as "0.5 ppm" until the next autosave round-trip; with it,
      // restore/import-commit immediately matches the eventual saved form.
      const newFormulation: FormulationEntry[] = detail.items
        .filter((i) => i.category === 'FLAVOR')
        .map((i) => {
          const rawUnit = (i.amountUnit ?? 'ppm') as AmountUnit;
          const norm = normalizeAmountUnit(i.amountValue ?? 0, rawUnit);
          return {
            compoundId: `${i.substanceKind}:${i.substanceId}`,
            ppm: norm.value,
            amountUnit: norm.unit as FormulationEntry['amountUnit'],
          };
        });
      if (activeVariant) {
        patchVariant(activeVariant.id, { formulation: newFormulation });
        // Tell autosave the variant is already server-confirmed so the
        // patch doesn't schedule a redundant POST + formulas refetch.
        autosave.markSynced({ ...activeVariant, formulation: newFormulation });
      }
    },
    [activeVariant, patchVariant, autosave],
  );

  // After a successful version restore, patch the active variant's formulation.
  const handleRestoreSuccess = useCallback(
    (detail: VersionDetail) => applyVersionDetail(detail),
    [applyVersionDetail],
  );

  // Replacement preview handler. Must sit above the early returns at the
  // bottom of this function — moving it below would skip the useCallback
  // on the bounce/no-session render and produce a "rendered fewer hooks
  // than expected" error.
  const onPreview = useCallback(
    (next: ReplacementPreview | null) => {
      setPreview(next);
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
      if (next == null) {
        setPreviewViz(null);
        return;
      }
      if (
        validId == null ||
        activeVariant?.formulaId == null ||
        activeVariant?.currentVersionId == null
      ) {
        return;
      }
      const parsedFrom = parseSubstanceKey(next.fromId);
      if (!parsedFrom) return;
      const toItems: Array<{ ndbTable: "compound" | "food"; rowid: number }> = [];
      for (const id of next.toIds) {
        const parsed = parseSubstanceKey(id);
        if (!parsed) continue;
        toItems.push({ ndbTable: parsed.kind, rowid: parsed.id });
      }
      const ac = new AbortController();
      previewAbortRef.current = ac;
      void fetchPreviewViz(
        {
          flavorId: validId,
          formulaId: activeVariant.formulaId,
          versionId: activeVariant.currentVersionId,
          from: { ndbTable: parsedFrom.kind, rowid: parsedFrom.id },
          toItems,
        },
        ac.signal,
      )
        .then((viz) => {
          if (ac.signal.aborted) return;
          setPreviewViz(viz);
        })
        .catch((err: unknown) => {
          if ((err as { name?: string })?.name === "AbortError") return;
          monitoring.warn("workbench_preview_viz_failed", err instanceof Error ? err : new Error(String(err)));
        });
    },
    [validId, activeVariant?.formulaId, activeVariant?.currentVersionId],
  );

  // After a successful import commit, patch the active variant's formulation.
  const handleImportCommitSuccess = useCallback(
    (detail: VersionDetail) => applyVersionDetail(detail),
    [applyVersionDetail],
  );

  if (shouldBounce) return <Navigate to="/" replace />;
  // Hydration covers the brief window where session has not yet resolved
  // (see isHydrating). Returning null here keeps WorkbenchTopBar/MakePhase
  // — which expect a non-null Session — from rendering against null.
  if (!session) return null;

  const handlePhaseChange = (next: Phase) => {
    if (next !== phase) {
      logEvent("flavor_workbench_phase_changed", {
        phase: next,
        flavor_id: validId,
      });
    }
    const params = new URLSearchParams(searchParams);
    if (next === "design") params.delete("phase");
    else params.set("phase", next);
    setSearchParams(params, { replace: true });
  };

  const handleChangeTarget = () => navigate("/flavors/new");

  // Star toggle for the workbench topbar. Optimistically flips the local
  // sessionStore boolean for instant feedback, fires the mutation so the
  // sidebar caches (recent / starred / search) stay in sync, and rolls
  // back the local flip on error.
  const handleToggleStar = () => {
    if (!validId) return;
    const next = !starred;
    toggleStarred();
    toggleStar.mutate(
      {
        id: validId,
        next,
        flavor: {
          id: validId,
          name: session.name ?? session.label ?? "",
          updatedAt: new Date().toISOString(),
        },
      },
      {
        onError: () => toggleStarred(),
      },
    );
  };

  const hideNew = phase === "make" || phase === "assess";

  // Flask handlers — write through to the active variant.
  // `unit` is optional so callers that only want to bump the amount (e.g.
  // sliders, recommendations) can keep the existing unit. FlaskRow's blur
  // path passes the normalized unit so the saved amountUnit migrates to
  // the best-fit unit in the same chain (ppm/ppb/ppt, mg/g/kg, μL/mL/L).
  const onChangeConc = (id: string, value: number, unit?: AmountUnit) => {
    setFormulation((prev) =>
      prev.map((x) =>
        x.compoundId === id
          ? unit !== undefined
            ? { ...x, ppm: value, amountUnit: unit }
            : { ...x, ppm: value }
          : x,
      ),
    );
  };
  const onRemove = (id: string) => {
    if (preview && preview.fromId === id) setPreview(null);
    setDemoRecViz(null); // DEMO (impl-demo-mock) — drop the applied-rec mock
    setFormulation((prev) => prev.filter((x) => x.compoundId !== id));
  };
  const onAdd = (id: string) => {
    // DEMO (impl-demo-mock) — apply the scenario CONC default. Clicking a
    // force-addable compound that's already in the flask edits its row to that
    // conc (e.g. δ-decalactone → 50 ppm) instead of being a no-op.
    const demoPpm = demoConcDefault(demoHint, id);
    setFormulation((prev) => {
      const existing = prev.find((x) => x.compoundId === id);
      if (existing) {
        if (demoPpm !== undefined) {
          return prev.map((x) =>
            x.compoundId === id ? { ...x, ppm: demoPpm } : x,
          );
        }
        // Re-adding an in-flask compound "doses it up" +10%. Only reachable
        // from the vertex-recommendation panel (Add-Ingredient in-flask rows
        // are disabled), so Add-Ingredient list semantics are unchanged. Inert
        // for non-demo flavors: a recommendation list never contains an
        // in-flask compound (the candidate pool excludes flask members).
        return prev.map((x) =>
          x.compoundId === id ? { ...x, ppm: x.ppm * 1.1 } : x,
        );
      }
      return [...prev, { compoundId: id, ppm: demoPpm ?? 1.0 }];
    });
  };
  // DEMO (impl-demo-mock) — Stage #10. Apply a vertex recommendation: do the
  // flask op via onAdd (add, or +10% dose-up if already present), then mock the
  // viz/loss move from the ORIGINAL pre-swap ingredient (origSubstanceId). A
  // /viz/recompute over {current flask + original ingredient} yields a
  // correctly-scaled radar + flavor-space direction; we override the loss with
  // the row's shown ΔLoss (the demo scripts the live loss, so a natural
  // recompute wouldn't reflect it) and rescale the flavor-space dot to that
  // loss. Single-step. Non-demo rows (no origSubstanceId) just add normally.
  // Remove with src/demo.
  const onApplyRecommendation = (row: VertexRecommendationRow) => {
    onAdd(row.compoundId);
    const origId = row.origSubstanceId;
    const formulaId = activeVariant?.formulaId ?? null;
    const versionId = activeVariant?.currentVersionId ?? null;
    if (
      origId == null ||
      validId == null ||
      formulaId == null ||
      versionId == null ||
      liveLoss == null
    ) {
      return;
    }
    // mockItems = current flask compounds + the original ingredient (deduped).
    const items: { substanceKind: "compound" | "food"; substanceId: number }[] = [];
    const seen = new Set<number>();
    for (const f of formulation) {
      const m = /^(compound|food):(\d+)$/.exec(f.compoundId);
      if (!m) continue;
      const sid = Number(m[2]);
      if (seen.has(sid)) continue;
      seen.add(sid);
      items.push({ substanceKind: m[1] as "compound" | "food", substanceId: sid });
    }
    if (!seen.has(origId)) {
      items.push({ substanceKind: "compound", substanceId: origId });
    }
    const newLoss = Math.max(0, liveLoss + row.deltaLoss);
    demoRecAbortRef.current?.abort();
    const ac = new AbortController();
    demoRecAbortRef.current = ac;
    void fetchRecompute(
      { flavorId: validId, formulaId, versionId, items },
      ac.signal,
    )
      .then(({ viz }) => {
        // Loss + flavor-space distance scripted to the shown ΔLoss; radar +
        // dot direction + detail axes come from the recompute (original
        // ingredient). Rescale the dot so ‖drCoord‖ == newLoss, keeping its
        // (original-ingredient) direction.
        const d = viz.drCoord;
        const mag = d ? Math.hypot(d.x, d.y, d.z ?? 0) : 0;
        const k = d && mag > 1e-9 ? newLoss / mag : 0;
        const drCoord = d
          ? { x: d.x * k, y: d.y * k, z: d.z == null ? null : d.z * k }
          : d;
        setDemoRecViz({ ...viz, loss: newLoss, drCoord });
      })
      .catch((err: unknown) => {
        if ((err as { name?: string } | null)?.name === "AbortError") return;
        monitoring.warn(
          "workbench_demo_rec_recompute_failed",
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };
  const onApplyPreview = () => {
    previewAbortRef.current?.abort();
    setPreviewViz(null);
    setDemoRecViz(null); // DEMO (impl-demo-mock) — drop the applied-rec mock
    if (!preview) return;
    setFormulation((prev) => {
      const src = prev.find((x) => x.compoundId === preview.fromId);
      const base = prev.filter((x) => x.compoundId !== preview.fromId);
      if (preview.toIds.length === 0) return base; // drop — remove source only
      // Carry the source row's amountUnit to the primary candidate so a mol%
      // row stays mol% after the swap. Additional compounds from a multi-
      // replacement seed at 1.0 ppm (placeholder, not a real amount).
      // Candidates already in the flask have their concentration INCREASED, not
      // duplicated — mergeFormulationAdditions enforces one row per compound.
      const additions: FormulationEntry[] = preview.toIds.map((id, i) => ({
        compoundId: id,
        ppm: i === 0 ? src?.ppm ?? 1.0 : 1.0,
        ...(i === 0 && src?.amountUnit ? { amountUnit: src.amountUnit } : {}),
      }));
      return mergeFormulationAdditions(base, additions);
    });
    setPreview(null);
  };
  const onCancelPreview = () => {
    previewAbortRef.current?.abort();
    setPreviewViz(null);
    setDemoRecViz(null); // DEMO (impl-demo-mock) — drop the applied-rec mock
    setPreview(null);
  };

  // Comparison swaps in for the FlaskPanel when at least one alt is picked
  // AND the variant isn't locked (locked = batch started → diff editing
  // makes no sense).
  const showComparePanel = compareSelected.length > 0 && !activeLocked;

  // When the active variant is locked, the design hides the compound search
  // entirely and lets the flask panel span the leftmost two columns. This
  // keeps the centre panel from collapsing into the wrong width when the
  // left column disappears.
  const flaskGridColumn = activeLocked ? "1 / span 2" : undefined;

  return (
    <div
      className="fade-in"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        flex: 1,
        minHeight: 0,
        // Workbench is design-locked at >=1240px; horizontal scroll keeps
        // the three-column layout intact on narrower viewports.
        overflow: "auto",
        minWidth: WORKBENCH_MIN_WIDTH,
      }}
    >
      <WorkbenchTopBar
        session={session}
        starred={starred}
        onToggleStar={handleToggleStar}
        phase={phase}
        onPhaseChange={handlePhaseChange}
        onChangeTarget={handleChangeTarget}
      />

      <FormulationTabsBar
        variants={variants}
        variantStats={variantStats}
        activeVariantId={activeVariantId}
        onActivate={setActiveVariantId}
        onFork={() => forkVariant(null)}
        onRename={renameVariant}
        onRemove={removeVariant}
        onPromptOpen={openPrompt}
        promptBtnRef={promptBtnRef}
        tabStyle={tabStyle}
        showKpi={tabShowKpi}
        hideNew={hideNew}
        lockedVariantIds={lockedVariantIds}
      />

      {phase === "design" ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              `minmax(280px, 320px) minmax(${FLASK_COL_MIN}px, 1fr) ${SENSORY_RAIL_WIDTH}px`,
            gap: 10,
            padding: 10,
            flex: 1,
            minHeight: 0,
            minWidth: WORKBENCH_MIN_WIDTH,
            background: "var(--bg)",
          }}
        >
          {/* LEFT — compound search. Hidden when locked so the flask can
              expand into both columns (matches design intent: once a batch
              is started, adding compounds offers no value). */}
          {!activeLocked && (
            <div
              className="vstack"
              style={{ gap: 12, minHeight: 0, height: "100%" }}
            >
              <div style={{ flex: 1, minHeight: 0 }}>
                {isHydrating ? (
                  <IngredientSearchSkeleton />
                ) : (
                  <IngredientSearch
                    flavorId={validId}
                    formulaId={activeVariant?.formulaId ?? null}
                    inFlask={formulation}
                    onAdd={onAdd}
                    // DEMO (impl-demo-mock) — routes recommendation clicks so
                    // the page mocks the viz/loss move from the original
                    // ingredient. Remove with services/demo.
                    onApplyRecommendation={onApplyRecommendation}
                    versionId={activeVariant?.currentVersionId ?? null}
                    previewId={reviewPreviewId}
                    // DEMO (impl-demo-mock) — pinned rows stay addable even when
                    // already in the flask; once added at their CONC default
                    // they're hidden from the list (via `hidden`). Remove with
                    // services/demo.
                    forceAddable={demoForceAddable}
                    hidden={demoHidden}
                    // Disabled while N-Block is computing or staging the
                    // proposed changes. Re-enabled once we land on the
                    // Review state so the user can hand-tweak before
                    // Accept/Reject.
                    disabled={
                      aiState.phase === "starting" ||
                      aiState.phase === "running" ||
                      aiState.phase === "applying" ||
                      aiState.phase === "saving"
                    }
                    // Show the "Recommended for vertex" cards during idle AND
                    // Review — in Review they recompute against the proposal
                    // via previewId, so they're no longer misleading. Still
                    // hidden during the AF animation phases and during a
                    // replacement dry-run preview.
                    radarSelection={
                      activeLocked ||
                      previewViz != null ||
                      (aiState.phase !== "idle" && aiState.phase !== "review")
                        ? null
                        : radarSelection
                    }
                  />
                )}
              </div>
            </div>
          )}

          {/* CENTER — Current flask, OR ComparePanel when alts are picked.
              When locked, expands to span both the left and centre columns. */}
          <div
            style={{
              minHeight: 0,
              minWidth: 0,
              display: "flex",
              gridColumn: flaskGridColumn,
            }}
          >
            <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
              {isHydrating ? (
                <FlaskPanelSkeleton />
              ) : showComparePanel ? (
                <ComparePanel
                  currentName={activeVariant.name}
                  currentFormulation={formulation}
                  currentLoss={liveLoss}
                  currentCost={liveCost}
                  selected={compareSelected}
                  computeStats={computeCompareStats}
                  catalog={compareCatalog}
                  onToggle={onCompareToggle}
                  onClear={onCompareClear}
                  onClearSlot={onCompareClearSlot}
                  suggestionsLoading={compareSuggestionsLoading}
                />
              ) : (
                <FlaskPanel
                  flavorId={validId}
                  formulaId={activeVariant.formulaId ?? null}
                  formulation={formulation}
                  foundation={activeVariant.fixedIngredients ?? []}
                  target={target}
                  // Loss readout in the AIBanner. Bridges to the AF
                  // state during applying / review so the tile decreases
                  // monotonically as N-Block stages changes, then pins
                  // to the proposal's final loss in review. During a user
                  // preview, shows the hypothetical swap's loss.
                  loss={
                    aiState.phase === "applying"
                      ? aiState.inflightViz?.loss ?? liveLoss
                      : aiState.phase === "review" || aiState.phase === "saving"
                        ? aiState.proposedViz?.loss ?? liveLoss
                        : demoRecViz?.loss ?? previewViz?.loss ?? liveLoss
                  }
                  cost={previewCost ?? liveCost}
                  useMedianFallback={useMedianFallback}
                  setUseMedianFallback={setUseMedianFallback}
                  medianPricePerGramUsd={medianPricePerGramUsd}
                  preview={preview}
                  locked={activeLocked}
                  onChangeConc={onChangeConc}
                  onRemove={onRemove}
                  onAdd={onAdd}
                  onPreview={onPreview}
                  onApplyPreview={onApplyPreview}
                  onCancelPreview={onCancelPreview}
                  onSeeBatchRecord={() => handlePhaseChange("make")}
                  showComposition={showComposition}
                  onToggleComposition={() => setShowComposition((v) => !v)}
                  hasComposition={hasComposition}
                  compositionRows={compositionRows}
                  compositionFoodName={compositionFoodName}
                  compositionNfoodVersionId={compositionNfoodVersionId}
                  compareCatalog={compareCatalog}
                  compareSelected={compareSelected}
                  onCompareToggle={onCompareToggle}
                  onCompareClear={onCompareClear}
                  computeCompareStats={computeCompareStats}
                  suggestionsLoading={compareSuggestionsLoading}
                  onRestoreSuccess={handleRestoreSuccess}
                  onImportCommitSuccess={handleImportCommitSuccess}
                  flavorName={session?.name ?? session?.label ?? undefined}
                  formulaName={activeVariant.name}
                  aiState={aiState}
                  onAutoFormulate={handleAutoFormulate}
                  // Auto-Formulate needs a TARGET flavor_substance to score
                  // against (it minimizes loss to that target). Disable the
                  // button when the active flavor has no target assigned and
                  // surface a tooltip pointing the user to Flavor Space.
                  autoFormulateDisabled={
                    activeLocked || aiState.phase !== "idle" || target == null
                  }
                  autoFormulateDisabledReason={
                    target == null
                      ? "Select a target flavor in Flavor Space to use Auto-Formulate"
                      : undefined
                  }
                  perItemByCompoundId={perItemByCompoundId}
                  // Target profile stays constant across AF phases — the
                  // formulation moves, the target doesn't.
                  targetProfile={liveViz?.target?.profile ?? null}
                  // Current profile drives the live Δ row + the "CURRENT"
                  // heatmap inside the AIBanner. During the apply phase
                  // we want it to move per step (interpolated viz from
                  // useAutoFormulate.inflightViz); during review it
                  // pins to the final proposedViz so the user sees the
                  // proposal's signature; otherwise it tracks the saved
                  // workbench viz.
                  currentProfileVec={
                    aiState.phase === "applying"
                      ? aiState.inflightViz?.profile ?? null
                      : aiState.phase === "review" || aiState.phase === "saving"
                        ? aiState.proposedViz?.profile ?? null
                        : liveViz?.profile ?? null
                  }
                />
              )}
            </div>
          </div>

          {isHydrating ? (
            <SensoryRailSkeleton />
          ) : (
            <SensoryAnalysisRail
              flavorId={validId}
              formulaId={activeVariant?.formulaId ?? null}
              versionId={activeVariant?.currentVersionId ?? null}
              comparisons={compareSensory}
              aiVizOverride={aiVizOverride}
              proposalMode={
                aiState.phase === "review" ||
                aiState.phase === "saving" ||
                previewViz != null
              }
              onActiveSelectionChange={handleActiveSelectionChange}
            />
          )}
        </div>
      ) : phase === "make" ? (
        // Pilot-locked: the phase transition still happens (so the user sees
        // there's a Make flow), but the content renders behind a barrier.
        <PilotBarrier fill>
          <MakePhase
            session={session}
            formulation={formulation}
            variantId={activeVariantId}
            variantName={activeVariant.name}
          />
        </PilotBarrier>
      ) : (
        <PilotBarrier fill>
          <AssessPhase variantName={activeVariant.name} />
        </PilotBarrier>
      )}

      {promptOpen && (
        <FormulationPromptPopover
          anchorRect={promptAnchorRect}
          onClose={() => setPromptOpen(false)}
          onSubmit={(text) => {
            setPromptOpen(false);
            forkVariant(text);
          }}
        />
      )}
    </div>
  );
}
